/* Importing the PropTypes module from the prop-types package. */
import PropTypes from 'prop-types';

/* Importing the components from the Material UI library. */
import { Checkbox, FormControlLabel, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';

/* Importing value sets. */
import { administrativeGender, maritalStatus } from '../../utils/Patient/Patient.utils.ValueSets';

SelectPatient.propTypes = {
  pagePatient: PropTypes.object,
};

/**
 * Renders the form for selecting a patient.
 * @param {Object} props - The component props.
 * @param {Object} props.pagePatient - The patient data.
 * @returns {JSX.Element} The rendered component.
 */
export default function SelectPatient(props) {
  const { pagePatient } = props;

  return (
    <form autoComplete="off">
      <Grid container item xs={12} columnSpacing={2} rowSpacing={2}>
        <Grid item xs={12} md={12}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                value={pagePatient.name_given}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="Middle Name"
                value={pagePatient['patient-mothersMaidenName']}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="Last Name"
                value={pagePatient.name_family}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="Suffix"
                value={pagePatient.name_suffix}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Date of Birth"
                value={pagePatient.birthDate}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="Birth Place"
                value={pagePatient['patient-birthPlace']}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Nationality"
                value={pagePatient['patient-nationality']}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                select
                label="Civil Status"
                value={pagePatient.maritalStatus_code}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              >
                {maritalStatus.map((el) => (
                  <MenuItem key={el.code} value={el.code}>
                    {el.display}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Gender"
            value={pagePatient.gender}
            style={{ border: 'none' }}
            variant="standard"
            InputProps={{ readOnly: true }}
          >
            {administrativeGender.map((el) => (
              <MenuItem key={el.code} value={el.code}>
                {el.display}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid container item xs={12} sm={6} alignItems="center">
          <FormControlLabel
            control={<Checkbox name="notification" checked={pagePatient['patient-pregnancyStatus']} readOnly />}
            label="Pregnant"
            disabled={pagePatient.gender !== 'female'}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ pt: 5, pb: 1 }}>
          Contact Information
        </Typography>
      </Grid>

      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Contact No."
                value={pagePatient.telecom_phone_value || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="Email Address"
                value={pagePatient.telecom_email_value || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ pt: 1 }}>
            Address
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Building/House No., Street, Barangay"
                value={pagePatient.address_line || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="City"
                value={pagePatient.address_city || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="Country/State"
                value={pagePatient.address_country || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="ZIP Code"
                value={pagePatient.address_postalCode || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ pt: 1 }}>
            Emergency Contact
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Emergency Contact's Name"
                value={pagePatient.contact_name_text || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="Emergency Contact's No."
                value={pagePatient.contact_telecom_value || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={6}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Relationship to the Patient"
                value={pagePatient.contact_relationship_text || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ pt: 5, pb: 1 }}>
          Other Information
        </Typography>
      </Grid>

      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="GSIS No."
                value={pagePatient['GSISNo-id'] || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />

              <TextField
                fullWidth
                label="SSS No."
                value={pagePatient['SSSNo-id'] || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="PhilHealth No."
                value={pagePatient['philhealthNo-id'] || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="HMO"
                value={pagePatient['patient-hmo'] || ''}
                style={{ border: 'none' }}
                variant="standard"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
}
